import { Component, signal, inject, OnInit, OnDestroy, HostListener, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Observable, map, filter, Subscription, skip } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SidebarNavComponent } from './shared/sidebar-nav/sidebar-nav.component';
import { LanguageSwitcherComponent } from './shared/language-switcher/language-switcher.component';
import { NotificationBadgeComponent } from './features/notifications/notification-badge.component';
import { NotificationPanelComponent } from './features/notifications/notification-panel.component';
import { AuthService } from './core/auth.service';
import { TokenService } from './core/token.service';
import { LocaleService, LOCALE_OPTIONS, SupportedLocale } from './core/locale.service';
import { ThemeService } from './core/theme.service';
import { DarkModeService } from './core/dark-mode.service';
import { VersionBadgeComponent } from './shared/version-badge/version-badge.component';

/**
 * Root application component for the Tenant Portal.
 *
 * Renders the two-column shell layout:
 *   - Skip-to-main-content link (keyboard accessibility)
 *   - <app-sidebar-nav> (left column, 240px on desktop; fixed overlay on mobile)
 *   - Overlay div (closes sidebar on tap outside on mobile)
 *   - Header with user email and logout button
 *   - <main id="main-content"> with <router-outlet> (right column)
 *
 * Requirements: 8.5, 8.6
 */
@Component({
    selector: 'app-root',
    imports: [RouterOutlet, AsyncPipe, SidebarNavComponent, LanguageSwitcherComponent, NotificationBadgeComponent, NotificationPanelComponent, TranslateModule, VersionBadgeComponent],
    template: `
    <a href="#main-content" class="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-b-md focus:text-sm focus:font-semibold">
      {{ 'common.skipToContent' | translate }}
    </a>

    @if (showShell()) {
      <div class="flex h-full bg-gray-50 dark:bg-gray-900">
        <!-- Sidebar -->
        <aside
          #sidebarEl
          class="w-60 min-w-[240px] bg-gray-900 dark:bg-gray-950 h-full overflow-y-auto
                 max-md:fixed max-md:left-[-240px] max-md:top-0 max-md:z-50
                 max-md:transition-[left] max-md:duration-200 max-md:ease-in-out
                 md:max-lg:w-[200px] md:max-lg:min-w-[200px]"
          [class.max-md:!left-0]="sidebarOpen()"
          [attr.aria-label]="'common.siteNavigation' | translate"
        >
          <app-sidebar-nav [isOpen]="sidebarOpen()" (hamburgerToggle)="onHamburgerToggle()" />
        </aside>

        <!-- Backdrop overlay (z above the sticky header z-40 so it dims the
             header on mobile; sidebar z-50 stays above the backdrop) -->
        <div
          class="hidden fixed inset-0 bg-black/50 z-[45]"
          [class.!block]="sidebarOpen()"
          (click)="closeSidebar()"
          aria-hidden="true"
        ></div>

        <!-- Content area -->
        <div class="flex-1 flex flex-col overflow-hidden">
          <header class="flex items-center px-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-14 sticky top-0 z-40">
            <button
              #hamburgerBtn
              class="hidden max-md:inline-flex items-center justify-center p-2 text-gray-700 dark:text-gray-200
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2 rounded"
              type="button"
              [attr.aria-label]="'common.openNavMenu' | translate"
              (click)="onHamburgerToggle()"
            >☰</button>
            <div class="flex-1"></div>
            <app-language-switcher class="max-md:hidden" />
            <app-notification-badge (panelToggled)="toggleNotificationPanel()" />
            <button
              class="p-2 rounded text-gray-700 dark:text-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2"
              type="button"
              [attr.aria-label]="darkModeService.isDark() ? ('common.switchToLight' | translate) : ('common.switchToDark' | translate)"
              (click)="darkModeService.toggle()"
            >
              @if (darkModeService.isDark()) { ☀️ } @else { 🌙 }
            </button>
            @if (userEmail$ | async; as email) {
              <span class="text-sm text-gray-500 dark:text-gray-400 mr-4 overflow-hidden text-ellipsis whitespace-nowrap max-w-[250px] max-md:hidden">
                {{ email }}
              </span>
            }
            <button
              class="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-md
                     hover:bg-red-600 hover:text-white transition-colors duration-150
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 focus-visible:outline-offset-2
                     dark:text-red-400 dark:border-red-400 dark:hover:bg-red-400 dark:hover:text-white"
              type="button"
              (click)="onLogout()"
              [attr.aria-label]="'common.logOut' | translate"
            >{{ 'common.logOut' | translate }}</button>
          </header>

          <div aria-live="polite" class="sr-only">{{ localeChangeAnnouncement() }}</div>

          <main id="main-content" tabindex="-1" class="flex-1 overflow-y-auto p-6 max-md:px-4 bg-gray-50 dark:bg-gray-900">
            <router-outlet />
          </main>
        </div>
      </div>

      <!-- Notification panel (slide-out) -->
      <app-notification-panel
        [isOpen]="notificationPanelOpen()"
        (closed)="closeNotificationPanel()"
      />
    } @else {
      <main id="main-content" class="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
        <router-outlet />
      </main>
    }

    <!-- Build/version badge: click for version + release date + stale-build check. -->
    <app-version-badge />
  `,
    styles: []
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
  title = 'Tenant Portal';

  private readonly authService = inject(AuthService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly localeService = inject(LocaleService);
  private readonly translateService = inject(TranslateService);

  /** ThemeService — injected to ensure theme CSS custom properties are applied on bootstrap */
  readonly themeService = inject(ThemeService);

  /** DarkModeService — exposed for template binding (dark mode toggle) */
  readonly darkModeService = inject(DarkModeService);

  private localeSubscription: Subscription | null = null;

  /** Reference to the sidebar aside element for focus management */
  @ViewChild('sidebarEl') sidebarEl!: ElementRef<HTMLElement>;

  /** Reference to the hamburger button for returning focus on close */
  @ViewChild('hamburgerBtn') hamburgerBtn!: ElementRef<HTMLButtonElement>;

  /** Tracks whether the mobile sidebar is open. */
  readonly sidebarOpen = signal<boolean>(false);

  /** Tracks whether the notification panel is open. */
  readonly notificationPanelOpen = signal<boolean>(false);

  /** Whether to show the full shell (sidebar + header). Hidden on auth/registration pages. */
  readonly showShell = signal<boolean>(true);

  /** ARIA live region announcement text for locale changes */
  readonly localeChangeAnnouncement = signal<string>('');

  /** Tracks whether focus management is pending after sidebar opens */
  private focusPending = false;

  /**
   * Observable of the user's email extracted from the access token JWT payload.
   * Emits null when not authenticated.
   */
  readonly userEmail$: Observable<string | null> = this.tokenService.idToken$.pipe(
    map((token) => this.extractDisplayNameFromToken(token))
  );

  constructor() {
    // Track route changes to show/hide the shell
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const url = event.urlAfterRedirects || event.url;
      // Compare the path only — the registration page may carry a query string
      // (e.g. /registration?step=plan) or fragment, which must still hide the shell.
      const path = url.split(/[?#]/)[0];
      const isAuthPage = path.startsWith('/auth/') || path === '/registration';
      this.showShell.set(!isAuthPage);
    });
  }

  ngOnInit(): void {
    // Subscribe to locale changes (skip the initial value) and announce to screen readers
    this.localeSubscription = this.localeService.locale$.pipe(
      skip(1) // Skip the initial locale — only announce actual changes
    ).subscribe((locale) => {
      this.announceLocaleChange(locale);
    });
  }

  ngOnDestroy(): void {
    this.localeSubscription?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.focusPending && this.sidebarOpen() && this.sidebarEl) {
      this.focusPending = false;
      this.moveFocusToSidebar();
    }
  }

  /** Close sidebar when Escape key is pressed and sidebar is open */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.sidebarOpen()) {
      this.closeSidebar();
    }
  }

  /** Trap focus within the sidebar when it's open as overlay on mobile */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.sidebarOpen() || !this.sidebarEl) {
      return;
    }

    const sidebar = this.sidebarEl.nativeElement;
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(sidebar.querySelectorAll<HTMLElement>(focusableSelectors));

    if (focusableElements.length === 0) {
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift+Tab: if focus is on first element, wrap to last
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab: if focus is on last element, wrap to first
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  onHamburgerToggle(): void {
    const wasOpen = this.sidebarOpen();
    this.sidebarOpen.update(v => !v);

    if (!wasOpen) {
      // Sidebar is now opening — schedule focus move
      this.focusPending = true;
    } else {
      // Sidebar is now closing — return focus to hamburger button
      this.returnFocusToHamburger();
    }
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
    this.returnFocusToHamburger();
  }

  toggleNotificationPanel(): void {
    this.notificationPanelOpen.update(v => !v);
  }

  closeNotificationPanel(): void {
    this.notificationPanelOpen.set(false);
  }

  onLogout(): void {
    this.authService.signOut();
  }

  /**
   * Announce the locale change to screen readers via the ARIA live region.
   * Uses the translation key 'language.changed' with the native language name,
   * resolved in the NEW locale so the announcement is in the target language.
   */
  private announceLocaleChange(locale: SupportedLocale): void {
    const option = LOCALE_OPTIONS.find(o => o.code === locale);
    const languageName = option?.label ?? locale;
    // Use instant() which resolves from the already-loaded new locale
    const announcement = this.translateService.instant('language.changed', { language: languageName });
    this.localeChangeAnnouncement.set(announcement);
  }

  /**
   * Extract the email claim from a JWT access token payload.
   * Returns null if the token is null or doesn't contain an email claim.
   */
  private extractDisplayNameFromToken(token: string | null): string | null {
    if (!token) {
      return null;
    }
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(payload));

      // Prefer human-friendly profile claims (present on the ID token) over the
      // raw Cognito username. For a Google-federated user `cognito:username` is
      // the provider-prefixed value (e.g. "Google_11367058…"), which we must
      // never show — so `email` and any `Google_…`-style username are treated
      // as last-resort / rejected fallbacks.
      const fullName = decoded.name?.trim();
      if (fullName) {
        return fullName;
      }

      const given = decoded.given_name?.trim();
      const family = decoded.family_name?.trim();
      const composed = [given, family].filter(Boolean).join(' ').trim();
      if (composed) {
        return composed;
      }

      if (decoded.email) {
        return decoded.email;
      }

      // preferred_username is a friendly handle only when it's not the raw
      // federated username (which starts with "<Provider>_").
      const preferred = decoded.preferred_username?.trim();
      if (preferred && !this.isFederatedUsername(preferred)) {
        return preferred;
      }

      const username = (decoded['cognito:username'] || decoded.username || '').trim();
      if (username && !this.isFederatedUsername(username)) {
        return username;
      }

      // Only a raw federated username is left — don't show "Google_<sub>".
      return null;
    } catch {
      return null;
    }
  }

  /** True for a raw federated Cognito username like "Google_1136…" / "Facebook_…". */
  private isFederatedUsername(value: string): boolean {
    return /^[A-Za-z0-9]+_[A-Za-z0-9]+$/.test(value) && value.includes('_');
  }

  /**
   * Move focus to the first focusable element inside the sidebar.
   * Called after the sidebar opens on mobile.
   */
  private moveFocusToSidebar(): void {
    const sidebar = this.sidebarEl.nativeElement;
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const firstFocusable = sidebar.querySelector<HTMLElement>(focusableSelectors);
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      // Fallback: focus the sidebar itself
      sidebar.setAttribute('tabindex', '-1');
      sidebar.focus();
    }
  }

  /**
   * Return focus to the hamburger button after sidebar closes.
   */
  private returnFocusToHamburger(): void {
    if (this.hamburgerBtn) {
      // Use setTimeout to ensure DOM has updated after sidebar closes
      setTimeout(() => this.hamburgerBtn.nativeElement.focus(), 0);
    }
  }
}
